import { useState, useCallback } from "react";

const useFormState = (initialValues = {}, validationRules = {}) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});

  const setField = useCallback((field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (prev[field]) {
        const next = { ...prev };
        delete next[field];
        return next;
      }
      return prev;
    });
  }, []);

  const handleChange = useCallback(
    (field) => (e) => {
      setField(field, e.target.value);
    },
    [setField]
  );

  const handleSelect = useCallback(
    (field) => (e) => {
      setField(field, e.value);
    },
    [setField]
  );

  const reset = useCallback((newValues) => {
    setValues(newValues || initialValues);
    setErrors({});
  }, [initialValues]);

  const validate = useCallback(() => {
    const newErrors = {};
    for (const [field, rules] of Object.entries(validationRules)) {
      const value = values[field];
      for (const rule of rules) {
        const error = rule(value, values);
        if (error) {
          newErrors[field] = error;
          break;
        }
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [values, validationRules]);

  const isValid = Object.keys(errors).length === 0;

  return {
    values,
    errors,
    setField,
    handleChange,
    handleSelect,
    reset,
    validate,
    isValid,
  };
};

export default useFormState;
